import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, Info, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import productService from "@/services/productService";
import storageLocationService from "@/services/storageLocationService";

const EMPTY_FORM = {
  product_id: "",
  storage_location_id: "",
  lot_code: "",
  manufacture_date: "",
  expiration_date: "",
  current_quantity: "",
  min_quantity: "",
  unit_cost: "",
};

/** "2026-10-24T00:00:00" → "2026-10-24" (valor do input date) */
const toDateInput = (value) => (value ? String(value).split("T")[0] : "");

/** Sugestão de lote para nova entrada: L + data de hoje (ex.: L20261021) */
const suggestLotCode = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `L${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
};

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p className="flex items-center gap-1 text-sm text-destructive">
      <AlertCircle className="h-4 w-4 flex-shrink-0" />
      {message}
    </p>
  );
}

/**
 * Modal para criar (Nova Entrada) ou ajustar um lote de estoque.
 *
 * Props: `open`, `onOpenChange`, `stock` (null = criação), `onSave(payload)`,
 * `loading` e `products` (catálogo já carregado pela tela; evita outro GET).
 */
const StockFormModal = ({
  open,
  onOpenChange,
  stock,
  onSave,
  loading = false,
  products: productsCatalog = [],
}) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const isEdit = !!stock;

  // Reset do formulário ao abrir (criação) ou trocar o lote (edição)
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setFormData(
      stock
        ? {
            product_id: String(stock.product_code || ""),
            storage_location_id: String(stock.storage_location_id || ""),
            lot_code: stock.lot_code || "",
            manufacture_date: toDateInput(stock.manufacture_date),
            expiration_date: toDateInput(stock.expiration_date),
            current_quantity: String(stock.current_quantity ?? ""),
            min_quantity: String(stock.min_quantity ?? ""),
            unit_cost: String(stock.unit_cost ?? ""),
          }
        : { ...EMPTY_FORM, lot_code: suggestLotCode() }
    );
  }, [open, stock]);

  // Dados auxiliares dos selects ao abrir
  useEffect(() => {
    if (!open) return;
    fetchStorageLocations();
    if (!stock) {
      if (productsCatalog.length > 0) {
        setProducts(productsCatalog);
      } else {
        fetchProducts();
      }
    }
  }, [open]);

  /**
   * Produtos da empresa, inclusive indisponíveis: produto com estoque zerado
   * fica available:false e some de GET /products (cardápio), mas precisa
   * aparecer aqui para ser reposto.
   */
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const data = await productService.getAdminAll();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      // Modal global já exibido pelo interceptor
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchStorageLocations = async () => {
    setLoadingLocations(true);
    try {
      const data = await storageLocationService.getAllStorageLocations();
      setStorageLocations(Array.isArray(data) ? data : []);
    } catch (error) {
      // Modal global já exibido pelo interceptor
      setStorageLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(({ [field]: _, ...rest }) => rest);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!isEdit && !formData.product_id) {
      newErrors.product_id = "Selecione o produto";
    }
    if (!formData.storage_location_id) {
      newErrors.storage_location_id = "Selecione o local de armazenamento";
    }
    if (!formData.lot_code.trim()) {
      newErrors.lot_code = "Informe o código do lote";
    } else if (formData.lot_code.trim().length > 50) {
      newErrors.lot_code = "Use no máximo 50 caracteres";
    }
    if (!formData.manufacture_date) {
      newErrors.manufacture_date = "Informe a data de fabricação";
    }
    if (!formData.expiration_date) {
      newErrors.expiration_date = "Informe a data de validade";
    } else if (
      formData.manufacture_date &&
      formData.expiration_date <= formData.manufacture_date
    ) {
      newErrors.expiration_date = "A validade deve ser depois da fabricação";
    }

    const quantity = Number(formData.current_quantity);
    if (
      formData.current_quantity === "" ||
      !Number.isInteger(quantity) ||
      quantity < 0
    ) {
      newErrors.current_quantity = "Informe um número inteiro (0 ou mais)";
    } else if (isEdit && quantity < Number(stock.reserved_quantity || 0)) {
      newErrors.current_quantity = `Não pode ficar abaixo das ${stock.reserved_quantity} un. reservadas para pedidos`;
    }

    const minQuantity = Number(formData.min_quantity || 0);
    if (!Number.isInteger(minQuantity) || minQuantity < 0) {
      newErrors.min_quantity = "Informe um número inteiro (0 ou mais)";
    }

    const unitCost = Number(formData.unit_cost);
    if (formData.unit_cost === "" || Number.isNaN(unitCost) || unitCost <= 0) {
      newErrors.unit_cost = "Informe um custo maior que zero";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // reserved_quantity não vai no payload: quem controla são os pedidos
    // (correção pelo menu "Corrigir reservas do produto")
    const payload = {
      storage_location_id: formData.storage_location_id,
      lot_code: formData.lot_code.trim(),
      manufacture_date: formData.manufacture_date,
      expiration_date: formData.expiration_date,
      current_quantity: Number(formData.current_quantity),
      min_quantity: Number(formData.min_quantity || 0),
      unit_cost: Number(formData.unit_cost),
    };
    // product_id (code UUID) só na criação: o produto do lote não muda
    if (!isEdit) payload.product_id = formData.product_id;

    onSave(payload);
  };

  const selectedProduct = products.find(
    (p) => String(p.id) === String(formData.product_id)
  );
  const selectedLocation = storageLocations.find(
    (l) => String(l.id) === String(formData.storage_location_id)
  );
  const quantity = Number(formData.current_quantity) || 0;
  const unitCost = Number(formData.unit_cost) || 0;
  const reserved = Number(stock?.reserved_quantity || 0);

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? "Ajustar lote" : "Nova entrada de estoque"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `${stock.product_name || "Produto"} · Lote #${stock.id}`
              : "Registre um novo lote de produto no estoque."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Produto */}
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="product_id">
                Produto <span className="text-destructive">*</span>
              </Label>
              {isEdit ? (
                <>
                  <Input
                    id="product_id"
                    value={stock.product_name || "Produto não identificado"}
                    disabled
                  />
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Info className="h-3.5 w-3.5 flex-shrink-0" />
                    O produto não pode ser alterado depois da entrada. Preço de
                    venda: {formatCurrency(stock.product_price)}
                  </p>
                </>
              ) : (
                <>
                  <Select
                    value={formData.product_id}
                    onValueChange={(value) => handleChange("product_id", value)}
                    disabled={loadingProducts}
                  >
                    <SelectTrigger
                      id="product_id"
                      className={cn(errors.product_id && "border-destructive")}
                    >
                      <SelectValue
                        placeholder={
                          loadingProducts
                            ? "Carregando produtos..."
                            : "Selecione um produto"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={String(product.id)}>
                          {product.name} · {formatCurrency(product.price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError message={errors.product_id} />
                  {selectedProduct && (
                    <p className="text-xs text-muted-foreground">
                      Preço de venda: {formatCurrency(selectedProduct.price)} ·
                      Em estoque hoje: {Number(selectedProduct.quantity || 0)}{" "}
                      un.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Local de armazenamento */}
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="storage_location_id">
                Local de armazenamento <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.storage_location_id}
                onValueChange={(value) =>
                  handleChange("storage_location_id", value)
                }
                disabled={loadingLocations}
              >
                <SelectTrigger
                  id="storage_location_id"
                  className={cn(
                    errors.storage_location_id && "border-destructive"
                  )}
                >
                  <SelectValue
                    placeholder={
                      loadingLocations
                        ? "Carregando locais..."
                        : "Selecione um local"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {storageLocations.map((location) => (
                    <SelectItem key={location.id} value={String(location.id)}>
                      {location.name}
                      {location.equipment_name
                        ? ` · ${location.equipment_name}`
                        : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError message={errors.storage_location_id} />
              {selectedLocation && selectedLocation.capacity != null && (
                <p className="text-xs text-muted-foreground">
                  Capacidade: {selectedLocation.capacity} · Livre:{" "}
                  {selectedLocation.capacity -
                    (selectedLocation.used_capacity || 0)}
                </p>
              )}
            </div>

            {/* Lote */}
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="lot_code">
                Código do lote <span className="text-destructive">*</span>
              </Label>
              <Input
                id="lot_code"
                placeholder="Ex.: L20261021"
                maxLength={50}
                value={formData.lot_code}
                onChange={(e) => handleChange("lot_code", e.target.value)}
                className={cn(errors.lot_code && "border-destructive")}
              />
              <FieldError message={errors.lot_code} />
            </div>

            {/* Datas */}
            <div className="space-y-2">
              <Label htmlFor="manufacture_date">
                Fabricação <span className="text-destructive">*</span>
              </Label>
              <Input
                id="manufacture_date"
                type="date"
                value={formData.manufacture_date}
                onChange={(e) =>
                  handleChange("manufacture_date", e.target.value)
                }
                className={cn(errors.manufacture_date && "border-destructive")}
              />
              <FieldError message={errors.manufacture_date} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiration_date">
                Validade <span className="text-destructive">*</span>
              </Label>
              <Input
                id="expiration_date"
                type="date"
                min={formData.manufacture_date || undefined}
                value={formData.expiration_date}
                onChange={(e) =>
                  handleChange("expiration_date", e.target.value)
                }
                className={cn(errors.expiration_date && "border-destructive")}
              />
              <FieldError message={errors.expiration_date} />
            </div>

            {/* Quantidades */}
            <div className="space-y-2">
              <Label htmlFor="current_quantity">
                Estoque físico (un.) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="current_quantity"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                placeholder="0"
                value={formData.current_quantity}
                onChange={(e) =>
                  handleChange("current_quantity", e.target.value)
                }
                className={cn(errors.current_quantity && "border-destructive")}
              />
              <FieldError message={errors.current_quantity} />
              {isEdit && (
                <p className="text-xs text-muted-foreground">
                  Reservadas: {reserved} un. · Disponível:{" "}
                  {Math.max(quantity - reserved, 0)} un.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="min_quantity">Estoque mínimo (un.)</Label>
              <Input
                id="min_quantity"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                placeholder="0"
                value={formData.min_quantity}
                onChange={(e) => handleChange("min_quantity", e.target.value)}
                className={cn(errors.min_quantity && "border-destructive")}
              />
              <FieldError message={errors.min_quantity} />
              {!errors.min_quantity && (
                <p className="text-xs text-muted-foreground">
                  Abaixo disso o lote aparece como "Baixo".
                </p>
              )}
            </div>

            {/* Custo */}
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="unit_cost">
                Custo unitário (R$) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="unit_cost"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                placeholder="0,00"
                value={formData.unit_cost}
                onChange={(e) => handleChange("unit_cost", e.target.value)}
                className={cn(errors.unit_cost && "border-destructive")}
              />
              <FieldError message={errors.unit_cost} />
              {unitCost > 0 && quantity > 0 && (
                <p className="text-sm font-semibold text-primary">
                  Valor do lote: {formatCurrency(unitCost * quantity)}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : isEdit ? (
                "Salvar ajuste"
              ) : (
                "Registrar entrada"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StockFormModal;
